import type { AccuReferenceFormState } from '../accuReferenceFormData'
import { LAB_ACCOUNT_SYSTEMS } from '../formData'
import type { OrderPdfData, OrderPdfTest, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import type { CatalogTest } from '../shared/useTestCatalog'

export function mapAccuReferenceToPdf(
  form: AccuReferenceFormState,
  auto: AutoPopulateData,
  testMap?: Map<string, CatalogTest>,
): OrderPdfData {
  // Resolve selected tests from the catalog map
  const selectedTests: OrderPdfTest[] = form.selectedTests.map((code) => {
    const test = testMap?.get(code)
    return {
      code,
      name: test?.name || code,
      category: test?.categoryDisplay || test?.category || 'General',
      specimen: test?.specimen,
    }
  })

  // Deduplicate by code
  const seen = new Set<string>()
  const uniqueTests = selectedTests.filter((t) => {
    // `code` is optional on OrderPdfTest (GTI has none); AccuReference always sets it.
    const key = t.code ?? t.name
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  const vendorSections: OrderPdfSection[] = []

  // Specimen collection
  const specimenData: Record<string, string> = {}
  if (form.specimenCollectionDate) specimenData['Collection Date'] = form.specimenCollectionDate
  if (form.specimenCollectionTime)
    specimenData['Collection Time'] =
      `${form.specimenCollectionTime} ${form.specimenCollectionAmPm}`
  if (form.isStat) specimenData['STAT'] = 'Yes'
  if (form.isFasting) specimenData['Fasting'] = 'Yes'
  if (form.dispatchPhlebotomist) specimenData['Dispatch Phlebotomist'] = 'Yes'
  if (Object.keys(specimenData).length > 0) {
    vendorSections.push({ title: 'Specimen Collection', type: 'keyValue', data: specimenData })
  }

  // Microbiology source
  if (form.microbiologySource) {
    vendorSections.push({
      title: 'Microbiology Specimen Source',
      type: 'text',
      content: form.microbiologySource,
    })
  }

  // Gynecology details
  const gynData: Record<string, string> = {}
  if (form.gynSource) gynData['Source'] = form.gynSource
  if (form.gynLmp) gynData['LMP'] = form.gynLmp
  if (form.gynBiopsySites.length > 0) gynData['Biopsy Sites'] = form.gynBiopsySites.join(', ')
  if (Object.keys(gynData).length > 0) {
    vendorSections.push({ title: 'Gynecology Details', type: 'keyValue', data: gynData })
  }

  // Parse ICD-10 diagnoses from free text
  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  return {
    vendorName: 'AccuReference Medical Lab',
    orderTitle: 'General Test Requisition',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.ACCU_REFERENCE] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.primaryCarrier ? auto.insurance.policyholderName : '',
      relationship: auto.insurance.primaryCarrier ? auto.insurance.relationship : '',
    },
    icd10Codes,
    selectedTests: uniqueTests,
    vendorSections,
    signatureDataUrl: form.signatureDataUrl,
    signatureDate: form.signatureDate,
  }
}
